import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { videos, articleContent } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const video = videos.find((v) => v.id === id) || videos[0];
  const article = articleContent[id || ''] || articleContent['v1'] || {
    title: video.title,
    author: '棒棒TV编辑部',
    publishDate: video.updateDate,
    category: video.category,
    tags: video.tags,
    summary: video.description,
    content: `<p>${video.description}</p>`,
    relatedVideos: [],
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: video.category, href: `/category/${video.categorySlug}` },
    { label: video.title },
  ];

  const relatedVideos = videos.filter((v) => article.relatedVideos.includes(v.id));

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    description: article.summary,
    articleBody: article.content.replace(/<[^>]*>/g, '').substring(0, 500),
    publisher: {
      '@type': 'Organization',
      name: '棒棒TV',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={article.title}
        description={article.summary.substring(0, 160)}
        keywords={[...article.tags, '棒棒TV', '免费高清视频'].join(',')}
        canonicalUrl={`https://www.bangbangtv.com/article/${id}`}
        ogType="article"
        articlePublishedTime={article.publishDate}
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              <article className="flex-1 min-w-0">
                <div className="relative aspect-video rounded-lg overflow-hidden bg-background-200 mb-6">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    title={`${video.title} - 棒棒TV免费在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 flex items-center gap-3">
                    <button className="w-14 h-14 flex items-center justify-center bg-primary-500 hover:bg-primary-600 text-white rounded-full cursor-pointer transition-colors">
                      <i className="ri-play-fill text-2xl ml-1"></i>
                    </button>
                    <span className="text-white text-sm font-medium">点击播放</span>
                  </div>
                </div>

                <header className="mb-6">
                  <h1 className="text-xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                    {video.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {article.publishDate}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {video.views}次播放
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500"></i>
                      {video.rating}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                </header>

                <div className="bg-background-100 rounded-lg p-5 md:p-8 mb-8">
                  <h2 className="text-lg font-semibold text-foreground-900 mb-3 font-heading">内容简介</h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{article.summary}</p>
                </div>

                <div className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed mb-10">
                  <div dangerouslySetInnerHTML={{ __html: article.content }} />
                </div>

                <div className="flex items-center gap-3 pt-6 border-t border-background-200">
                  <span className="text-sm text-foreground-500 whitespace-nowrap">分享：</span>
                  <button className="w-9 h-9 flex items-center justify-center bg-background-100 hover:bg-primary-50 text-foreground-600 hover:text-primary-500 rounded-md cursor-pointer transition-colors">
                    <i className="ri-wechat-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center bg-background-100 hover:bg-primary-50 text-foreground-600 hover:text-primary-500 rounded-md cursor-pointer transition-colors">
                    <i className="ri-weibo-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center bg-background-100 hover:bg-primary-50 text-foreground-600 hover:text-primary-500 rounded-md cursor-pointer transition-colors">
                    <i className="ri-link"></i>
                  </button>
                </div>
              </article>

              <aside className="lg:w-80 xl:w-96 shrink-0">
                <div className="sticky top-24">
                  <div className="bg-background-100 rounded-lg p-4 md:p-5 mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <i className="ri-film-line text-primary-500"></i>
                      <h3 className="text-sm font-semibold text-foreground-900">影视信息</h3>
                    </div>
                    <div className="flex flex-col gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-foreground-500">类型</span>
                        <span className="text-foreground-800">{video.category}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground-500">年份</span>
                        <span className="text-foreground-800">{video.year}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground-500">时长</span>
                        <span className="text-foreground-800">{video.duration}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground-500">评分</span>
                        <span className="text-accent-600 font-semibold">{video.rating}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground-500">播放量</span>
                        <span className="text-foreground-800">{video.views}</span>
                      </div>
                    </div>
                  </div>

                  {relatedVideos.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-3">相关推荐</h3>
                      <div className="flex flex-col gap-3">
                        {relatedVideos.map((rv) => (
                          <Link key={rv.id} to={`/article/${rv.id}`} className="flex gap-3 group cursor-pointer">
                            <div className="w-28 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                              <img src={rv.thumbnail} alt={rv.title} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <div className="min-w-0">
                              <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                                {rv.title}
                              </h4>
                              <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
                                <span>{rv.category}</span>
                                <span className="flex items-center gap-0.5">
                                  <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                                  {rv.rating}
                                </span>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}